import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Card,
  CardContent,
  Typography,
  Divider,
  Box,
  Alert,
  CircularProgress,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  LinearProgress,
  Button,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  Search as SearchIcon,
  RemoveCircle as RemoveCircleIcon
} from '@mui/icons-material';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const ProjectedTax = () => {
  const [taxData, setTaxData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  
  // ========== FINANCIAL YEAR STATE ==========
  const [selectedFinancialYear, setSelectedFinancialYear] = useState('');
  const [availableYears, setAvailableYears] = useState([]);

  // Generate financial years (last 10 years)
  useEffect(() => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = 0; i < 10; i++) {
      const year = currentYear - i;
      years.push(`${year}-${String(year + 1)}`);
    }
    setAvailableYears(years);
    
    // Set current financial year as default
    const today = new Date();
    const currentMonth = today.getMonth();
    const currentYearNum = today.getFullYear();
    let defaultFY;
    if (currentMonth >= 3) {
      defaultFY = `${currentYearNum}-${String(currentYearNum + 1)}`;
    } else {
      defaultFY = `${currentYearNum - 1}-${String(currentYearNum)}`;
    }
    setSelectedFinancialYear(defaultFY);
  }, []);

  // ========== FETCH TAX DATA - CALLED ON BUTTON CLICK ==========
  const fetchProjectedTax = async () => {
    if (!selectedFinancialYear) {
      setError('Please select a financial year');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setTaxData(null);

      const token = localStorage.getItem('token');
      if (!token) {
        alert("No authentication token found. Please log in.");
        return;
      }

      const response = await axios.get(`${API_URL}/api/payroll/tax/projectedtax`, {
        params: {
          financial_year: selectedFinancialYear
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
        withCredentials: true
      });
      
      
      if (response.data.success) {
        setTaxData(response.data.data);
        setError('');
      } else {
        setError(response.data.message || 'Failed to fetch tax data');
      }
    } catch (error) {
      if (error.response && error.response.status === 403) {
        setError(error.response.data.message);
      } else {
        setError('Failed to fetch projected tax data. Please try again.');
        setTaxData(null);
      }
    } finally {
      setLoading(false);
    }
  };

  // Format currency
  const formatCurrency = (value) => {
    const numValue = Number(value);
    if (isNaN(numValue) || numValue === 0) {
      return '₹0';
    }
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
      minimumFractionDigits: 0
    }).format(numValue);
  };

  // ========== UPDATED STATUS CHIP WITH N/A SUPPORT ==========
  const getStatusChip = (item) => {
    if (!item.isApplicable) {
      return (
        <Tooltip title="No data available for this month">
          <Chip 
            icon={<RemoveCircleIcon />} 
            label="N/A" 
            color="default" 
            size="small" 
            variant="outlined"
            sx={{ opacity: 0.6 }}
          />
        </Tooltip>
      );
    }
    
    if (item.isPaid) {
      return (
        <Tooltip title="Payslip generated and tax deducted">
          <Chip 
            icon={<CheckCircleIcon />} 
            label="Paid" 
            color="success" 
            size="small" 
          />
        </Tooltip>
      );
    }
    
    if (item.status === 'Projected (Missing)') {
      return (
        <Tooltip title="Expected data but not found">
          <Chip 
            icon={<WarningIcon />} 
            label="Missing" 
            color="warning" 
            size="small" 
          />
        </Tooltip>
      );
    }
    
    return (
      <Tooltip title="Estimated tax for upcoming month">
        <Chip 
          icon={<PendingIcon />} 
          label="Projected" 
          color="info" 
          size="small" 
        />
      </Tooltip>
    );
  };

  if (error && !taxData) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert 
          severity="error" 
          action={
            <Button color="inherit" size="small" onClick={fetchProjectedTax}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      </Box>
    );
  }

  const { employee, summary, monthly_breakdown, salary_details, financial_year, better_regime, calculation_mode } = taxData || {};

  // ========== FILTER MONTHS BASED ON APPLICABILITY ==========
  const applicableMonths = monthly_breakdown?.filter(m => m.isApplicable) || [];
  const paidMonths = applicableMonths.filter(m => m.isPaid) || [];
  const projectedMonths = applicableMonths.filter(m => !m.isPaid) || [];
  const nonApplicableMonths = monthly_breakdown?.filter(m => !m.isApplicable) || [];

  // ========== GET SUMMARY STATS ==========
  const totalApplicableMonths = applicableMonths.length;
  const totalPaidMonths = paidMonths.length;
  const totalProjectedMonths = projectedMonths.length;
  const totalNonApplicableMonths = nonApplicableMonths.length;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight="bold">
          Projected Tax Details
        </Typography>
      </Box>

      {/* ========== SEARCH SECTION ========== */}
      <Paper sx={{ p: 3, mb: 3, boxShadow: 2 }}>
        <Grid container spacing={3} alignItems="flex-end">
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel id="fy-select-label">Select Financial Year</InputLabel>
              <Select
                labelId="fy-select-label"
                value={selectedFinancialYear}
                onChange={(e) => setSelectedFinancialYear(e.target.value)}
                label="Select Financial Year"
              >
                {availableYears.map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Button 
              
              variant="contained" 
              
              onClick={fetchProjectedTax}
              disabled={loading || !selectedFinancialYear}
         
              sx={{ height: 56 }}
            >
              {loading ? 'Loading...' : 'View Projected Tax'}
            </Button>
          </Grid>
        </Grid>

        {error && (
          <Box mt={2}>
            <Alert severity="error" onClose={() => setError('')}>
              {error}
            </Alert>
          </Box>
        )}
      </Paper>

      {/* ========== DATA DISPLAY ========== */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '40vh' }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Loading projected tax data...</Typography>
        </Box>
      ) : taxData ? (
        <>
          <Typography variant="subtitle1" color="text.secondary" gutterBottom>
            Financial Year: {financial_year}
          </Typography>

          {/* Employee Details */}
          <Card sx={{ mt: 2, mb: 3, boxShadow: 3 }}>
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="caption" color="text.secondary">Employee ID</Typography>
                  <Typography variant="body1" fontWeight="bold">{employee?.employee_id}</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="caption" color="text.secondary">Name</Typography>
                  <Typography variant="body1" fontWeight="bold">{employee?.firstName} {employee?.lastName}</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="caption" color="text.secondary">Email</Typography>
                  <Typography variant="body1" fontWeight="bold">{employee?.companyemail}</Typography>
                </Grid>
            
              </Grid>
            </CardContent>
          </Card>

          {/* ========== MONTH STATS SUMMARY ========== */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={3}>
              <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#e3f2fd' }}>
                <Typography variant="caption" color="text.secondary">Total Months</Typography>
                <Typography variant="h6" fontWeight="bold">{monthly_breakdown?.length || 0}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#e8f5e9' }}>
                <Typography variant="caption" color="text.secondary">Applicable Months</Typography>
                <Typography variant="h6" fontWeight="bold" color="success.main">{totalApplicableMonths}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#ffebee' }}>
                <Typography variant="caption" color="text.secondary">N/A Months</Typography>
                <Typography variant="h6" fontWeight="bold" color="error.main">{totalNonApplicableMonths}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#fff3e0' }}>
                <Typography variant="caption" color="text.secondary">Working Months</Typography>
                <Typography variant="h6" fontWeight="bold" color="warning.main">
                  {summary?.total_months_working || 0}
                </Typography>
              </Paper>
            </Grid>
          </Grid>



          {/* Progress Bar */}
          <Card sx={{ mb: 3, boxShadow: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Tax Collection Progress
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  {summary?.total_months_paid || 0} / {summary?.total_months_working || 12} months paid
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={((summary?.total_months_paid || 0) / (summary?.total_months_working || 12)) * 100} 
                sx={{ height: 10, borderRadius: 5 }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                <Typography variant="caption" color="success.main">
                  Paid: {summary?.total_months_paid || 0} months
                </Typography>
                <Typography variant="caption" color="info.main">
                  Remaining: {summary?.total_months_remaining || 0} months
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  N/A: {totalNonApplicableMonths} months
                </Typography>
              </Box>
            </CardContent>
          </Card>

          {/* ========== UPDATED MONTHLY BREAKDOWN TABLE WITH N/A SUPPORT ========== */}
          <Card sx={{ boxShadow: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Monthly Breakdown
                </Typography>
                <Box>
                  <Chip 
                    label={`${totalPaidMonths} Paid`} 
                    color="success" 
                    size="small" 
                    sx={{ mr: 1 }}
                  />
                  <Chip 
                    label={`${totalProjectedMonths} Projected`} 
                    color="info" 
                    size="small" 
                    sx={{ mr: 1 }}
                  />
                  <Chip 
                    label={`${totalNonApplicableMonths} N/A`} 
                    color="default" 
                    size="small" 
                    variant="outlined"
                  />
                </Box>
              </Box>

                          <Tabs 
                value={activeTab} 
                onChange={(e, v) => setActiveTab(v)} 
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{ mb: 2 }}
              >
                <Tab label={`All (${monthly_breakdown?.length || 0})`} />
                <Tab label={`Paid (${totalPaidMonths})`} />
                <Tab label={`Projected (${totalProjectedMonths})`} />
                <Tab label={`N/A (${totalNonApplicableMonths})`} />
              </Tabs>


              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 'bold' }}>Month</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>Gross Salary</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>Tax Deducted</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>Cumulative Gross</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>Cumulative Tax</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(activeTab === 0 ? monthly_breakdown : 
                      activeTab === 1 ? paidMonths : 
                      activeTab === 2 ? projectedMonths : 
                      nonApplicableMonths).map((item, index) => (
                      <TableRow 
                        key={index}
                        sx={{ 
                          bgcolor: !item.isApplicable ? 'rgba(200, 200, 200, 0.15)' : 
                                    item.isPaid ? 'rgba(232, 245, 233, 0.3)' : 
                                    'rgba(255, 255, 255, 1)',
                          '&:hover': { bgcolor: '#f5f5f5' },
                          opacity: !item.isApplicable ? 0.7 : 1
                        }}
                      >
                        <TableCell>
                          <Typography fontWeight={item.isPaid ? 'bold' : 'normal'}>
                            {item.month}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          {item.isApplicable ? formatCurrency(item.grossSalary) : '—'}
                        </TableCell>
                        <TableCell align="right">
                          {item.isApplicable ? formatCurrency(item.taxDeducted) : '—'}
                        </TableCell>
                        <TableCell align="right">
                          {item.isApplicable && item.cumulativeGross > 0 ? formatCurrency(item.cumulativeGross) : '—'}
                        </TableCell>
                        <TableCell align="right">
                          {item.isApplicable && item.cumulativeTax > 0 ? formatCurrency(item.cumulativeTax) : '—'}
                        </TableCell>
                        <TableCell align="center">
                          {getStatusChip(item)}
                        </TableCell>
                      </TableRow>
                    ))}

                    {activeTab === 0 && monthly_breakdown?.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          <Typography color="text.secondary">No data available</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* ========== UPDATED SUMMARY ROW ========== */}
              <Box sx={{ mt: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={3}>
                    <Typography variant="caption" color="text.secondary">Total Months</Typography>
                    <Typography variant="h6" fontWeight="bold">{monthly_breakdown?.length || 0}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Typography variant="caption" color="text.secondary">Applicable Months</Typography>
                    <Typography variant="h6" fontWeight="bold" color="success.main">{totalApplicableMonths}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Typography variant="caption" color="text.secondary">Total Gross Salary</Typography>
                    <Typography variant="h6" fontWeight="bold" color="primary">
                      {formatCurrency(applicableMonths.reduce((sum, m) => sum + m.grossSalary, 0))}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Typography variant="caption" color="text.secondary">Total Tax</Typography>
                    <Typography variant="h6" fontWeight="bold" color="secondary">
                      {formatCurrency(applicableMonths.reduce((sum, m) => sum + m.taxDeducted, 0))}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </CardContent>
          </Card>

   
        </>
      ) : (
        <Paper sx={{ p: 4, textAlign: 'center', boxShadow: 2 }}>
          <Typography variant="h6" color="text.secondary">
            Select a financial year and click "View Projected Tax" to see the details
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default ProjectedTax;